import React from "react";
import PropTypes from "prop-types";
import { partition } from "lodash";
import { getAllActiveRulesStrengths, getEnchancedRules } from "../../helpers";
import AcmgPill from "../../acmg-pill/AcmgPill";
import LinkifyBase from "../../../../../../components/social/LinkifyBase";
import { Table, ToggleButton } from "../../../../../../components/atoms";
import ls from "../../../../../../common/helpers/localStorageActions";

const AutomatedCriteriaTable = ({
  acmgRules,
  classifications,
  activeOverrideStrengths,
  showFailedCriteria,
  allRulesToggleState,
  onToggleHandle,
  onAcmgSelectChange,
  shouldShowACMGPoints,
  setFailedCriteria,
  rulesThatMetCriteria,
}) => {
  const getCriteriaTableExplanation = (cl) => {
    const {
      name,
      user_explain_failed: userExplainFailed,
      user_explain: userExplain,
      implemented_in_thalia: isImplemented,
    } = cl;

    const showNotImplemented = !isImplemented ? "Not implemented." : "-";

    if (userExplainFailed) {
      return userExplainFailed.map((explainFailed, id) => {
        const key = `${name}${id}${Date.now()}`;
        return <LinkifyBase key={key} txt={explainFailed} />;
      });
    }
    if (userExplain) {
      return userExplain.map((explain, id) => {
        const key = `${name}${id}${Date.now()}`;
        return (
          <div>
            <LinkifyBase key={key} txt={explain} />
          </div>
        );
      });
    }

    return showNotImplemented;
  };

  const rules = getEnchancedRules({
    acmgRules,
    classifications,
  });

  if (!rules) return null;

  const partitionedRules = partition(
    rules,
    (r) => r.met_criteria === false || !r.met_criteria
  );
  const enchancedAcmgRules = [...partitionedRules[1], ...partitionedRules[0]];

  const filteredRules = !showFailedCriteria
    ? enchancedAcmgRules.filter((f) => f.user_explain)
    : enchancedAcmgRules.filter((f) => f.implemented_in_thalia);

  const activeRules = getAllActiveRulesStrengths(
    activeOverrideStrengths,
    rulesThatMetCriteria
  );

  return (
    filteredRules &&
    filteredRules.length > 0 && (
      <Table
        headers={[
          { id: 1, title: "Rule" },
          {
            id: 2,
            title: (
              <div className="tw-flex tw-items-center tw-justify-between">
                Explanation{" "}
                <ToggleButton
                  onToggleClick={(e) => {
                    setFailedCriteria(e.target.checked);
                    ls.setAcmgFailedCriteria(e.target.checked);
                  }}
                  label="Show failed criteria"
                  isChecked={showFailedCriteria}
                  noPaddingAndMargin={true}
                />
              </div>
            ),
          },
        ]}
        rows={filteredRules.map((c) => ({
          id: c.name,
          columns: [
            {
              id: `${c.name}-rule`,
              content: (
                <AcmgPill
                  key={`${c.name}-${Date.now()}`}
                  classification={c}
                  hasMargins={false}
                  showFailedCriteria={false}
                  activeRules={activeRules}
                  rulesToggleState={allRulesToggleState}
                  onToggleHandle={onToggleHandle}
                  onSelectChange={onAcmgSelectChange}
                  shouldShowPointsReference={shouldShowACMGPoints}
                />
              ),
            },
            {
              id: `${c.name}-expl`,
              content: getCriteriaTableExplanation(c),
            },
          ],
        }))}
      />
    )
  );
};

AutomatedCriteriaTable.propTypes = {
  acmgRules: PropTypes.arrayOf(
    PropTypes.shape({
      evidence: PropTypes.string,
      met_criteria: PropTypes.bool,
      user_explain_failed: PropTypes.arrayOf(PropTypes.string),
      acmg_triggered_strength: PropTypes.string,
      name: PropTypes.string,
    })
  ),
  classifications: PropTypes.arrayOf(PropTypes.shape({})),
  activeOverrideStrengths: PropTypes.shape({}).isRequired,
  showFailedCriteria: PropTypes.bool.isRequired,
  allRulesToggleState: PropTypes.shape({}).isRequired,
  onToggleHandle: PropTypes.func.isRequired,
  onAcmgSelectChange: PropTypes.func.isRequired,
  shouldShowACMGPoints: PropTypes.bool.isRequired,
  setFailedCriteria: PropTypes.func.isRequired,
  rulesThatMetCriteria: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

AutomatedCriteriaTable.defaultProps = {
  acmgRules: undefined,
  classifications: undefined,
};

export default AutomatedCriteriaTable;
